import cx from 'classnames';
import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@gov-nx/ui/web/Icon/Icon';
import type { UrlChecker } from '@gov-nx/utils/web/utils/link';
import { isUrlExternal } from '@gov-nx/utils/web/utils/link';

export type MarkdownLinkProps = {
	href?: string;
	children?: React.ReactNode;
	linkClassName?: string;
	includeProtocolInLinkLabels?: boolean;
	openExternalLinksInSameTab?: boolean;
	forceFullPageNavigation?: boolean;
	checkIfUrlShouldOpenNewWindowWith?: UrlChecker;
	onInternalLinkClick?: (event: React.MouseEvent) => void;
};

/**
 * Renders an anchor produced by markdown. External links open in a new tab
 * (with an icon) by default; internal links use the router. Extracted from
 * MarkdownRender so the link behavior can be unit-tested in isolation.
 */
export const MarkdownLink = ({
	href,
	children,
	linkClassName,
	includeProtocolInLinkLabels,
	openExternalLinksInSameTab,
	forceFullPageNavigation,
	checkIfUrlShouldOpenNewWindowWith,
	onInternalLinkClick,
}: MarkdownLinkProps) => {
	if (!href) {
		// eslint-disable-next-line jsx-a11y/anchor-is-valid
		return <a>{children}</a>;
	}

	let linkLabel: string | null = null;

	// [https://example.com](https://example.com) => <a href="https://example.com">example.com</a>
	const hrefEqualsLabel = href === `${children}`;
	if (hrefEqualsLabel && !includeProtocolInLinkLabels) {
		linkLabel = `${children}`.replace(/^https?:\/\//i, '');
	}

	const label = linkLabel || children;

	if (isUrlExternal(href, checkIfUrlShouldOpenNewWindowWith)) {
		const externalClassName = cx('text-(--text-primary-color)!', linkClassName);

		if (openExternalLinksInSameTab) {
			return (
				<a
					className={externalClassName}
					href={href}>
					{label}
				</a>
			);
		}

		return (
			<a
				className={externalClassName}
				href={href}
				target="_blank"
				rel="noreferrer">
				{label}
				<Icon
					name="box-arrow-up-right"
					type="components"
					size="m"
					className="relative top-0.5 ml-2!"
				/>
			</a>
		);
	}

	if (forceFullPageNavigation) {
		return (
			<a
				className={cx(linkClassName)}
				href={href}
				onClick={onInternalLinkClick}>
				{label}
			</a>
		);
	}

	return (
		<Link
			onClick={onInternalLinkClick}
			className={cx(linkClassName)}
			to={href}>
			{label}
		</Link>
	);
};
